// Pure helpers for the Reports (client analytics) view. Kept out of the
// component so the top-10 + "Others" roll-up and the matrix reconstruction
// are unit-testable without rendering.

export const MD_MINUTES = 480; // one man-day = 8h
export const toMD = (mins) => mins / MD_MINUTES;
// Always man-days to one decimal — hours never appear on this page.
export const fmtMD = (mins) => `${toMD(mins).toFixed(1)} MD`;

// Fixed cap for every breakdown; the remainder rolls into a single "Others"
// row. No expand/collapse, no search — the cap is fixed.
export const TOP_N = 10;

/**
 * Split ranked rows into the visible top-N and a rolled-up tail.
 * Returns { shown, others, othersMins }. `othersMins` sums the tail so the
 * rows still reconcile to the period total.
 */
export function rollupTopN(rows = [], n = TOP_N) {
  const shown = rows.slice(0, n);
  const others = rows.slice(n);
  const othersMins = others.reduce((s, r) => s + r.mins, 0);
  return { shown, others, othersMins };
}

/**
 * Rebuild the matrix accessor object from the serialised aggregation. The
 * server sends a flat `cells` map (keyed "client||dept") plus the byClient /
 * byDept totals; this reconstructs get / clientTotal / deptTotal so the
 * component reads like the design reference.
 *
 * Totals come from byClient / byDept — i.e. the FULL month — so the matrix's
 * totals row reconciles even when only the top-N clients are shown.
 */
export function buildMatrix(agg) {
  const m = agg?.matrix || {};
  const cells = m.cells || {};
  const clientTotals = new Map((agg?.byClient || []).map((c) => [c.key, c.mins]));
  const deptTotals = new Map((agg?.byDept || []).map((d) => [d.key, d.mins]));
  return {
    clients: m.clients || [], // sorted desc by total
    depts: m.depts || [],
    maxCell: m.maxCell || 1,
    get: (client, dept) => cells[`${client}||${dept}`] || 0,
    clientTotal: (client) => clientTotals.get(client) || 0,
    deptTotal: (dept) => deptTotals.get(dept) || 0,
  };
}

/**
 * Mint heat-shade for a matrix cell: alpha ramps with intensity relative to
 * the busiest cell, capped so the darkest cell stays readable.
 */
export function heatBg(mins, maxCell) {
  if (mins <= 0) return 'transparent';
  const a = Math.min(0.62, 0.06 + (mins / maxCell) * 0.56);
  return `rgba(0, 193, 193, ${a.toFixed(3)})`;
}
